import React, { useEffect, useRef } from 'react';
import { Transition } from 'react-transition-group';
import cx from 'classnames';
import useWidget from '@/widget/ui/hooks/useWidget';
import useNewMessageNotifier from '@/widget/ui/hooks/useNewMessageNotifier';
import { useChatWidgetContext } from '@/widget/services/useChatWidgetContext';
import { useStore } from 'zustand';
import styles from './ChatWidget.module.scss';

import InvitationButtons from './InvitationButtons';
import WidgetBody from './WidgetBody';

function ChatWidget() {
  const { store, settings } = useChatWidgetContext();
  const open = useStore(store, (state) => state.open);
  const { position, isMobile, host } = settings;

  const { initWSConnection, connected } = useWidget();
  const { init: initNewMessageNotifier } = useNewMessageNotifier(host);
  const wrapper = useRef(null);
  const bodyRef = useRef(null);
  const buttonsRef = useRef(null);

  useEffect(() => {
    initWSConnection();
    initNewMessageNotifier();
  }, [initWSConnection, initNewMessageNotifier, store]);

  useEffect(() => {
    window.parent.postMessage({
      action: 's2Chat:containerProps',
      position,
      isMobile,
      open,
    }, '*');
  }, [open, isMobile, position]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const rect = wrapper.current ? wrapper.current.getBoundingClientRect() : { width: 0, height: 0 };

      window.parent.postMessage({
        action: 's2Chat:containerResize',
        width: rect.width,
        height: rect.height,
      }, '*');
    });

    if (wrapper.current) { resizeObserver.observe(wrapper.current); }
    return () => resizeObserver.disconnect();
  }, [connected, wrapper]);

  const classes = cx(
    's2-widget-container',
    styles.container,
    styles[position],
    {
      [styles.open]: open,
      [styles.mobile]: isMobile,
    },
  );

  return (
    <>
      {connected && (
        <div ref={wrapper} className={classes}>
          <Transition
            appear
            nodeRef={bodyRef}
            in={open}
            timeout={300}
            mountOnEnter
            unmountOnExit
          >
            {(state) => (
              <div
                ref={bodyRef}
                className={cx('s2-widget-body', styles.body, styles[state], { [styles.mobile]: isMobile })}
              >
                {state === 'entered' && <WidgetBody />}
              </div>
            )}
          </Transition>
          <Transition
            appear
            nodeRef={buttonsRef}
            in={!open}
            timeout={300}
            mountOnEnter
            unmountOnExit
          >
            {(state) => (
              <div
                ref={buttonsRef}
                className={cx('s2-widget-channels', styles.invitation, styles[state])}
              >
                <InvitationButtons />
              </div>
            )}
          </Transition>
        </div>
      )}
    </>
  );
}

export default ChatWidget;
